/**
 * Shared constants for audit/versioning functionality
 * Used across stores and components to ensure consistency
 */

// Action labels for audit display (scope-specific)
export const AUDIT_ACTION_LABELS = {
  story: {
    create: 'CREATED',
    update: 'UPDATED',
    destroy: 'DESTROYED'
  },
  attachment: {
    create: 'ADDED',
    update: 'MODIFIED',
    destroy: 'REMOVED'
  }
}

// Human-readable labels for story field names displayed in the audit UI
export const AUDIT_FIELD_LABELS = {
  slug: 'Slug',
  headline: 'Headline',
  drop_head: 'Deck head',
  web_head: 'Web headline',
  web_slug: 'Web slug',
  web_summary: 'Web summary',
  web_body: 'Web body',
  byline: 'Byline',
  content: 'Content',
  endnote: 'Endnote',
  notes: 'Notes',
  credits: 'Credits',
  overline: 'Overline',
  keywords: 'Keywords',
  infobox_headline: 'Breakout box headline',
  infobox_text: 'Breakout box text',
  pub_date: 'Pub date',
  squeue_id: 'Status queue',
  user_id: 'Owner',
  featured: 'Featured',
  ispublic: 'Free',
  comments_disabled: 'Comments disabled',
  freelance: 'Freelance',
  saved: 'Saved',
  // StoryMedium (attachment) fields
  medium_id: 'Media file',
  for_print: 'For print',
  for_web: 'For web',
  use_as_content_for: 'Used as content for',
  list_order: 'Order',
  file_mode: 'File mode',
  twitter_feed: 'Twitter feed',
  tweet: 'Tweet',
  google_search_ad: 'Google Search Ad',
  outbrain_ad: 'Outbrain Ad'
}

// Fields that are internal/system fields and shouldn't be shown to users in audit history
export const AUDIT_IGNORED_FIELDS = [
  'locker_id',
  'lock_version',
  'locked_at',
  'updated_at',
  'created_at',
  'path',
  'content_path',
  'words',
  'inches',
  'media_count',
  'api_foreign_key'
]

// Fields that contain HTML content and need special diff handling
export const AUDIT_HTML_FIELDS = [
  'content',
  'byline',
  'web_summary',
  'web_body',
  'endnote',
  'infobox_text'
]

// Fields that represent boolean values stored as integers
export const AUDIT_BOOLEAN_FIELDS = [
  'ispublic',
  'featured',
  'comments_disabled',
  'freelance',
  'saved',
  'twitter_feed',
  'tweet',
  'google_search_ad',
  'outbrain_ad'
]

/**
 * Get the human-readable label for a field name
 * Falls back to title-casing the field name if no label is defined
 * @param {string} field - The field name
 * @returns {string} The human-readable label
 */
export function humanizeFieldName(field) {
  return AUDIT_FIELD_LABELS[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

/**
 * Check if a field should be ignored in audit displays
 * @param {string} field - The field name
 * @returns {boolean} True if the field should be ignored
 */
export function isIgnoredField(field) {
  return AUDIT_IGNORED_FIELDS.includes(field)
}

/**
 * Get the appropriate action label for an audit based on scope
 * @param {string} action - The action (create, update, destroy)
 * @param {string} scope - The scope (story, attachment)
 * @returns {string} The human-readable action label
 */
export function getActionLabel(action, scope = 'story') {
  return AUDIT_ACTION_LABELS[scope]?.[action] || action.toUpperCase()
}

/**
 * Filter out ignored fields from an audited_changes object
 * @param {Object} auditedChanges - The audited_changes from an audit record
 * @returns {Object} The filtered changes with only meaningful fields
 */
export function getMeaningfulChanges(auditedChanges) {
  if (!auditedChanges) return {}
  
  return Object.fromEntries(
    Object.entries(auditedChanges)
      .filter(([field]) => !isIgnoredField(field))
  )
}
